import { useEffect, useRef } from 'react';
import { UseFormWatch, FieldValues } from 'react-hook-form';

/**
 * Custom hook that debounces the react-hook-form watch function
 * @param watch - The watch function from useForm
 * @param callback - The function to call with the debounced values
 * @param delay - Debounce delay in milliseconds
 * @returns Functions to manually trigger or cancel debouncing
 */
const useDebouncedWatch = <T extends FieldValues>(
  watch: UseFormWatch<T>,
  callback: (value: Partial<T>) => void,
  delay: number = 300,
) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // Store the latest callback in a ref to avoid stale closures
  const callbackRef = useRef(callback);

  // Update the callback ref when it changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Clear the timeout when unmounting
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const subscription = watch((value) => {
      // Clear previous timeout
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Set new timeout
      timerRef.current = setTimeout(() => {
        callbackRef.current(value);
      }, delay);
    });

    // Cleanup function
    return () => {
      subscription.unsubscribe();
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [watch, delay]);
};

export default useDebouncedWatch;
